import apiService from './api-service';
import { utilsService } from "./utils-service";
import { filterService } from "./filter-service";

const FUEL_REPORT_NAME = "fuelReport";
const FUEL_CHARGES_REPORT_NAME = "fuelChargesReport";
const FUEL_INPUT_DOCUMENTS_REPORT_NAME = "fuelInputDocumentsReport";
const NOTIFICATIONS_REPORT_NAME = "notificationsReport";
const FUEL_LIMITS_REPORT_NAME = "fuelLimitsReport";
const DIGITAL_DEVICES_REPORT_NAME = "digitalDevicesReport";
const AZK_FUEL_OUTPUT_DOCUMENTS_REPORT_NAME = "azkFuelOutputDocumentsReport";
const AZK_FUEL_CHANGE_DOCUMENTS_REPORT_NAME = "azkFuelChangeDocumentsReport";
const AZK_FUEL_INPUT_DOCUMENTS_REPORT_NAME = "azkFuelInputDocumentsReport";
const AZK_IMPULSE_DEVICE_REPORT_NAME = "azkImpulseDevicesReport";
const AZK_FUEL_REPORT_NAME = "azkFuelReport";

export const reportService = {
	FUEL_REPORT_NAME,
	FUEL_CHARGES_REPORT_NAME,
	FUEL_INPUT_DOCUMENTS_REPORT_NAME,
	NOTIFICATIONS_REPORT_NAME,
	FUEL_LIMITS_REPORT_NAME,
	DIGITAL_DEVICES_REPORT_NAME,
	AZK_FUEL_OUTPUT_DOCUMENTS_REPORT_NAME,
	AZK_FUEL_CHANGE_DOCUMENTS_REPORT_NAME,
	AZK_FUEL_INPUT_DOCUMENTS_REPORT_NAME,
	AZK_IMPULSE_DEVICE_REPORT_NAME,
	AZK_FUEL_REPORT_NAME,
	getReports,
	getFuelReport,
	getFuelChargesReport,
	getFuelInputDocumentsReport,
	getNotificationsReport,
	getFuelLimitsReport,
	getDigitalDevicesReport,
	getAzkFuelOutputDocumentsReport,
	getAzkFuelChangeDocumentsReport,
	getAzkFuelInputDocumentsReport,
	getAzkImpulseDeviceReport,
	getAzkFuelReport,
	createReport,
	updateReport,
	deleteReport,
	exportReport
};

async function getReports() {
	const reports = await apiService.get('report/getReports');

	return reports;
}

async function getFuelReport(beginDateTime, endDateTime, report, isAccountingSystem, fuelTanks) {
	const beginDateTimeFormatted = utilsService.formatDateTime(beginDateTime, utilsService.DATE_TIME_FORMAT3);
	const endDateTimeFormatted = utilsService.formatDateTime(endDateTime, utilsService.DATE_TIME_FORMAT3);

	let filterValues = [];

	if (report.filterKeys.selected == filterService.TANK_IDS_FILTER) {
		filterValues = filterService.getFilterValuesByFuelTanks(report.filterValues);
	} else if (report.filterKeys.selected == filterService.COMPANY_ID_FILTER) {
		filterValues = filterService.getFilterValuesByCompanies(fuelTanks, report.filterValues);
	} else if (report.filterKeys.selected == filterService.DEPO_FILTER) {
		filterValues = filterService.getFilterValuesByDepos(fuelTanks, report.filterValues);
	}

	const columns = [];

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		columns.push(report.columnsRight.values[i].value);
	}

	const parameters = {
		beginTime: beginDateTimeFormatted,
		endTime: endDateTimeFormatted,
		filters: {
			tankIds: filterValues
		},
		columns: columns,
		periodType: report.periodTypes.selected,
		group: report.groups.selected
	};

	const fuelReport = isAccountingSystem
		?
		await apiService.post('report/getFuelReportAccountingSystem', parameters)
		:
		await apiService.post('report/getFuelReport', parameters);

	return fuelReport;
}

async function getFuelChargesReport(beginDateTime, endDateTime, report, fuelTanks) {
	const beginDateTimeFormatted = utilsService.formatDateTime(beginDateTime, utilsService.DATE_TIME_FORMAT3);
	const endDateTimeFormatted = utilsService.formatDateTime(endDateTime, utilsService.DATE_TIME_FORMAT3);

	let filterName, filterValues = [];

	if (report.filterKeys.selected == filterService.TANK_IDS_FILTER) {
		filterName = filterService.TANK_IDS_FILTER;
		filterValues = filterService.getFilterValuesByFuelTanks(report.filterValues);
	} else if (report.filterKeys.selected == filterService.COMPANY_ID_FILTER) {
		filterName = filterService.TANK_IDS_FILTER;
		filterValues = filterService.getFilterValuesByCompanies(fuelTanks, report.filterValues);
	} else if (report.filterKeys.selected == filterService.DEPO_FILTER) {
		filterName = filterService.TANK_IDS_FILTER;
		filterValues = filterService.getFilterValuesByDepos(fuelTanks, report.filterValues);
	} else if (report.filterKeys.selected == filterService.DRIVER_IDS_FILTER) {
		filterName = filterService.DRIVER_IDS_FILTER;
		filterValues = filterService.getFilterValuesByDrivers(report.filterValues);
	} else if (report.filterKeys.selected == filterService.DEVICE_ID_FILTER) {
		filterName = filterService.DEVICE_ID_FILTER;
		filterValues = filterService.getFilterValuesByDevices(report.filterValues);
	}

	const columns = [];

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		columns.push(report.columnsRight.values[i].value);
	}

	const parameters = {
		beginTime: beginDateTimeFormatted,
		endTime: endDateTimeFormatted,
		filters: {
			[filterName]: filterValues
		},
		columns: columns,
		periodType: report.periodTypes.selected,
		group: report.groups.selected
	};

	const fuelReport = await apiService.post('report/getFuelChargesReport', parameters);

	return fuelReport;
}

async function getFuelInputDocumentsReport(beginDateTime, endDateTime, report) {
	const parameters = prepareParameters(beginDateTime, endDateTime, report);
	const fuelInputDocumentsReport = await apiService.post('report/getFuelInputDocumentsReport', parameters);

	return fuelInputDocumentsReport;
}

async function getNotificationsReport(beginDateTime, endDateTime, report) {
	const parameters = prepareParameters(beginDateTime, endDateTime, report);
	const notificationsReport = await apiService.post('report/getNotificationsReport', parameters);

	return notificationsReport;
}

async function getFuelLimitsReport(beginDateTime, endDateTime, report) {
	const beginDateTimeFormatted = utilsService.formatDateTime(beginDateTime, utilsService.DATE_TIME_FORMAT3);
	const endDateTimeFormatted = utilsService.formatDateTime(endDateTime, utilsService.DATE_TIME_FORMAT3);

	const driverIds = report.filterValues.values.map(x => x.key).join(",");

	const columns = [];

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		columns.push(report.columnsRight.values[i].value);
	}

	const parameters = {
		beginTime: beginDateTimeFormatted,
		endTime: endDateTimeFormatted,
		filters: {
			driverIds: driverIds
		},
		columns: columns,
		periodType: report.periodTypes.selected,
		group: report.groups.selected
	};

	const fuelLimitsReport = await apiService.post('report/getFuelLimitsReport', parameters);

	return fuelLimitsReport;
}

async function getDigitalDevicesReport(beginDateTime, endDateTime, report) {
	const parameters = prepareParameters(beginDateTime, endDateTime, report);
	const digitalDevicesReport = await apiService.post('report/getDigitalDevicesReport', parameters);

	return digitalDevicesReport;
}

async function getAzkFuelOutputDocumentsReport(beginDateTime, endDateTime, report, fuelTanks) {
	const beginDateTimeFormatted = utilsService.formatDateTime(beginDateTime, utilsService.DATE_TIME_FORMAT3);
	const endDateTimeFormatted = utilsService.formatDateTime(endDateTime, utilsService.DATE_TIME_FORMAT3);
	
	let filterValues = [];

	if (report.filterKeys.selected == filterService.TANK_IDS_FILTER) {
		filterValues = filterService.getFilterValuesByFuelTanks(report.filterValues);
	} else if (report.filterKeys.selected == filterService.COMPANY_ID_FILTER) {
		filterValues = filterService.getFilterValuesByCompanies(fuelTanks, report.filterValues);
	} else if (report.filterKeys.selected == filterService.DEPO_FILTER) {
		filterValues = filterService.getFilterValuesByDepos(fuelTanks, report.filterValues);
	}

	const columns = [];

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		columns.push(report.columnsRight.values[i].value);
	}

	const parameters = {
		beginTime: beginDateTimeFormatted,
		endTime: endDateTimeFormatted,
		filters: {
			tankIds: filterValues
		},
		columns: columns,
		periodType: report.periodTypes.selected,
		group: report.groups.selected
	};

	const fuelOutputDocumentsReport = await apiService.post('azk/getFuelOutputDocuments', parameters);

	return fuelOutputDocumentsReport;
}

async function getAzkFuelChangeDocumentsReport(beginDateTime, endDateTime, report, fuelTanks) {
	const beginDateTimeFormatted = utilsService.formatDateTime(beginDateTime, utilsService.DATE_TIME_FORMAT3);
	const endDateTimeFormatted = utilsService.formatDateTime(endDateTime, utilsService.DATE_TIME_FORMAT3);
	
	let filterValues = [];

	if (report.filterKeys.selected == filterService.TANK_IDS_FILTER) {
		filterValues = filterService.getFilterValuesByFuelTanks(report.filterValues);
	} else if (report.filterKeys.selected == filterService.COMPANY_ID_FILTER) {
		filterValues = filterService.getFilterValuesByCompanies(fuelTanks, report.filterValues);
	} else if (report.filterKeys.selected == filterService.DEPO_FILTER) {
		filterValues = filterService.getFilterValuesByDepos(fuelTanks, report.filterValues);
	}

	const columns = [];

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		columns.push(report.columnsRight.values[i].value);
	}

	const parameters = {
		beginTime: beginDateTimeFormatted,
		endTime: endDateTimeFormatted,
		filters: {
			tankIds: filterValues
		},
		columns: columns,
		periodType: report.periodTypes.selected,
		group: report.groups.selected
	};

	const fuelChangeDocumentsReport = await apiService.post('azk/getFuelChangeDocuments', parameters);

	return fuelChangeDocumentsReport;
}

async function getAzkFuelInputDocumentsReport(beginDateTime, endDateTime, report, fuelTanks) {
	const beginDateTimeFormatted = utilsService.formatDateTime(beginDateTime, utilsService.DATE_TIME_FORMAT3);
	const endDateTimeFormatted = utilsService.formatDateTime(endDateTime, utilsService.DATE_TIME_FORMAT3);
	
	let filterValues = [];

	if (report.filterKeys.selected == filterService.TANK_IDS_FILTER) {
		filterValues = filterService.getFilterValuesByFuelTanks(report.filterValues);
	} else if (report.filterKeys.selected == filterService.COMPANY_ID_FILTER) {
		filterValues = filterService.getFilterValuesByCompanies(fuelTanks, report.filterValues);
	} else if (report.filterKeys.selected == filterService.DEPO_FILTER) {
		filterValues = filterService.getFilterValuesByDepos(fuelTanks, report.filterValues);
	}

	const columns = [];

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		columns.push(report.columnsRight.values[i].value);
	}

	const parameters = {
		beginTime: beginDateTimeFormatted,
		endTime: endDateTimeFormatted,
		filters: {
			tankIds: filterValues
		},
		columns: columns,
		periodType: report.periodTypes.selected,
		group: report.groups.selected
	};

	const fuelInputDocumentsReport = await apiService.post('azk/getFuelInputDocuments', parameters);

	return fuelInputDocumentsReport;
}

async function getAzkFuelReport(beginDateTime, endDateTime, report, fuelTanks) {
	const beginDateTimeFormatted = utilsService.formatDateTime(beginDateTime, utilsService.DATE_TIME_FORMAT3);
	const endDateTimeFormatted = utilsService.formatDateTime(endDateTime, utilsService.DATE_TIME_FORMAT3);
	
	let filterValues = [];

	if (report.filterKeys.selected == filterService.TANK_IDS_FILTER) {
		filterValues = filterService.getFilterValuesByFuelTanks(report.filterValues);
	} else if (report.filterKeys.selected == filterService.COMPANY_ID_FILTER) {
		filterValues = filterService.getFilterValuesByCompanies(fuelTanks, report.filterValues);
	} else if (report.filterKeys.selected == filterService.DEPO_FILTER) {
		filterValues = filterService.getFilterValuesByDepos(fuelTanks, report.filterValues);
	}

	const columns = [];

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		columns.push(report.columnsRight.values[i].value);
	}

	const parameters = {
		beginTime: beginDateTimeFormatted,
		endTime: endDateTimeFormatted,
		filters: {
			tankIds: filterValues
		},
		columns: columns,
		periodType: report.periodTypes.selected,
		group: report.groups.selected
	};
	
	const fuelReport = await apiService.post('azk/getFuelReport', parameters);

	return fuelReport;
}

async function getAzkImpulseDeviceReport(beginDateTime, endDateTime, report) {
	const parameters = prepareParameters(beginDateTime, endDateTime, report);
	const impulseDevicesReport = await apiService.post('azk/getImpulseDeviceReport', parameters);

	return impulseDevicesReport;
}

function prepareParameters(beginDateTime, endDateTime, report) {
	const beginDateTimeFormatted = utilsService.formatDateTime(beginDateTime, utilsService.DATE_TIME_FORMAT3);
	const endDateTimeFormatted = utilsService.formatDateTime(endDateTime, utilsService.DATE_TIME_FORMAT3);

	const tankIds = report.filterValues.values.map(x => x.key).join(",");

	const columns = [];

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		columns.push(report.columnsRight.values[i].value);
	}

	const parameters = {
		beginTime: beginDateTimeFormatted,
		endTime: endDateTimeFormatted,
		filters: {
			tankIds: tankIds
		},
		columns: columns,
		periodType: report.periodTypes.selected,
		group: report.groups.selected
	};

	return parameters;
}

async function createReport(report, label) {
	const parameters = {
		id: report.id,
		label: label,
		settings: []
	};

	const createdReport = {
		name: report.name,
		label: label,
		settings: []
	};

	let currentIndex = 0;

	for (let i = 0; i < report.columnsLeft.values.length; i++) {
		parameters.settings.push({
			type: "column",
			name: report.columnsLeft.values[i].value,
			isActive: false,
			orderIndex: currentIndex
		});

		createdReport.settings.push({
			type: "column",
			name: report.columnsLeft.values[i].value,
			label: report.columnsLeft.values[i].text,
			isActive: false,
			orderIndex: currentIndex
		});

		currentIndex++;
	}

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		parameters.settings.push({
			type: "column",
			name: report.columnsRight.values[i].value,
			isActive: true,
			orderIndex: currentIndex
		});

		createdReport.settings.push({
			type: "column",
			name: report.columnsRight.values[i].value,
			label: report.columnsRight.values[i].text,
			isActive: true,
			orderIndex: currentIndex
		});

		currentIndex++;
	}

	currentIndex = 0;

	for (let i = 0; i < report.filterKeys.options.length; i++) {
		if (report.filterKeys.options[i].value != null) {
			const isActive = report.filterKeys.selected == report.filterKeys.options[i].value;

			const filterValue = {
				type: "array",
				data: []
			};

			for (let j = 0; j < report.filterValues.data.length; j++) {
				filterValue.data.push({
					isActive: report.filterValues.values.find(x => x.key == report.filterValues.data[j].key) != null,
					key: report.filterValues.data[j].key,
					value: report.filterValues.data[j].label
				})
			}

			parameters.settings.push({
				type: "filter",
				name: report.filterKeys.options[i].value,
				isActive: isActive,
				value: JSON.stringify(filterValue),
				orderIndex: currentIndex
			});

			createdReport.settings.push({
				type: "filter",
				name: report.filterKeys.options[i].value,
				label: report.filterKeys.options[i].text,
				isActive: isActive,
				value: JSON.stringify(filterValue),
				orderIndex: currentIndex
			});

			currentIndex++;
		}
	}

	currentIndex = 0;

	for (let i = 0; i < report.groups.options.length; i++) {
		if (report.groups.options[i].value != null) {
			const isActive = report.groups.selected == report.groups.options[i].value;

			parameters.settings.push({
				type: "group",
				name: report.groups.options[i].value,
				isActive: isActive,
				orderIndex: currentIndex
			});

			createdReport.settings.push({
				type: "group",
				name: report.groups.options[i].value,
				label: report.groups.options[i].text,
				isActive: isActive,
				orderIndex: currentIndex
			});

			currentIndex++;
		}
	}

	currentIndex = 0;

	for (let i = 0; i < report.periodTypes.options.length; i++) {
		if (report.periodTypes.options[i].value != null) {
			const isActive = report.periodTypes.selected == report.periodTypes.options[i].value;

			parameters.settings.push({
				type: "periodType",
				name: report.periodTypes.options[i].value,
				isActive: isActive,
				orderIndex: currentIndex
			});

			createdReport.settings.push({
				type: "periodType",
				name: report.periodTypes.options[i].value,
				label: report.periodTypes.options[i].text,
				isActive: isActive,
				orderIndex: currentIndex
			});

			currentIndex++;
		}
	}

	const result = await apiService.post('report/createReport', parameters);

	createdReport.id = result.data.reportId;

	return {
		isSuccess: result.isSuccess,
		data: createdReport
	};
}

async function updateReport(report) {
	const parameters = {
		id: report.id,
		name: report.name,
		label: report.label,
		settings: []
	};

	const updatedReport = {
		id: report.id,
		name: report.name,
		label: report.label,
		settings: []
	};

	let currentIndex = 0;

	for (let i = 0; i < report.columnsLeft.values.length; i++) {
		parameters.settings.push({
			type: "column",
			name: report.columnsLeft.values[i].value,
			isActive: false,
			orderIndex: currentIndex
		});

		updatedReport.settings.push({
			type: "column",
			name: report.columnsLeft.values[i].value,
			label: report.columnsLeft.values[i].text,
			isActive: false,
			orderIndex: currentIndex
		});

		currentIndex++;
	}

	for (let i = 0; i < report.columnsRight.values.length; i++) {
		parameters.settings.push({
			type: "column",
			name: report.columnsRight.values[i].value,
			isActive: true,
			orderIndex: currentIndex
		});

		updatedReport.settings.push({
			type: "column",
			name: report.columnsRight.values[i].value,
			label: report.columnsRight.values[i].text,
			isActive: true,
			orderIndex: currentIndex
		});

		currentIndex++;
	}

	currentIndex = 0;

	for (let i = 0; i < report.filterKeys.options.length; i++) {
		if (report.filterKeys.options[i].value != null) {
			const isActive = report.filterKeys.selected == report.filterKeys.options[i].value;

			const filterValue = {
				type: "array",
				data: []
			};

			for (let j = 0; j < report.filterValues.data.length; j++) {
				filterValue.data.push({
					isActive: report.filterValues.values.find(x => x.key == report.filterValues.data[j].key) != null,
					key: report.filterValues.data[j].key,
					value: report.filterValues.data[j].label
				})
			}

			parameters.settings.push({
				type: "filter",
				name: report.filterKeys.options[i].value,
				isActive: isActive,
				value: JSON.stringify(filterValue),
				orderIndex: currentIndex
			});

			updatedReport.settings.push({
				type: "filter",
				name: report.filterKeys.options[i].value,
				label: report.filterKeys.options[i].text,
				isActive: isActive,
				value: JSON.stringify(filterValue),
				orderIndex: currentIndex
			});

			currentIndex++;
		}
	}

	currentIndex = 0;

	for (let i = 0; i < report.groups.options.length; i++) {
		if (report.groups.options[i].value != null) {
			const isActive = report.groups.selected == report.groups.options[i].value;

			parameters.settings.push({
				type: "group",
				name: report.groups.options[i].value,
				isActive: isActive,
				orderIndex: currentIndex
			});

			updatedReport.settings.push({
				type: "group",
				name: report.groups.options[i].value,
				label: report.groups.options[i].text,
				isActive: isActive,
				orderIndex: currentIndex
			});

			currentIndex++;
		}
	}

	currentIndex = 0;

	for (let i = 0; i < report.periodTypes.options.length; i++) {
		if (report.periodTypes.options[i].value != null) {
			const isActive = report.periodTypes.selected == report.periodTypes.options[i].value;

			parameters.settings.push({
				type: "periodType",
				name: report.periodTypes.options[i].value,
				isActive: isActive,
				orderIndex: currentIndex
			});

			updatedReport.settings.push({
				type: "periodType",
				name: report.periodTypes.options[i].value,
				label: report.periodTypes.options[i].text,
				isActive: isActive,
				orderIndex: currentIndex
			});

			currentIndex++;
		}
	}

	const result = await apiService.post('report/updateReport', parameters);

	return {
		isSuccess: result.isSuccess,
		data: updatedReport
	};
}

async function deleteReport(report) {
	const parameters = {
		id: report.id
	};

	const deletedReport = await apiService.post('report/deleteReport', parameters);

	return deletedReport;
}

async function exportReport(format, reportName, name, periodType, subject, beginDateTime, endDateTime, headers, data, summary, beginDateTimeLabel, endDateTimeLabel) {
	const parameters = {
		format: format,
		reportName,
		name: name,
		periodType: periodType,
		subject: subject,
		beginTime: {
			label: beginDateTimeLabel,
			value: utilsService.formatDateTime(beginDateTime, utilsService.DATE_TIME_FORMAT3)
		},
		endTime: {
			label: endDateTimeLabel,
			value: utilsService.formatDateTime(endDateTime, utilsService.DATE_TIME_FORMAT3)
		},
		headers: headers,
		data: data,
		summary: summary
	}

	const result = await apiService.downloadFile('report/exportReport', parameters, `${reportName}.${format}`);

	return result;
}

